import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ServerImage from '../components/ServerImage';
import { IoIosLink } from "react-icons/io";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import axios from 'axios';
import { SERVER } from '../config/config';
import { IoIosCheckmarkCircle } from "react-icons/io";

const StudioDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [studioItem, setStudioItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Image Carousel Component
  const ImageCarousel = ({ images, altPrefix }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
      const checkMobile = () => {
        setIsMobile(window.innerWidth < 768);
      };
      
      checkMobile();
      window.addEventListener('resize', checkMobile);
      return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const imagesPerView = isMobile ? 1 : 3;
    const totalSlides = Math.max(0, images.length - imagesPerView + 1);

    const nextSlide = () => {
      setCurrentIndex((prev) => (prev + 1) % totalSlides);
    };

    const prevSlide = () => {
      setCurrentIndex((prev) => (prev - 1 + totalSlides) % totalSlides);
    };

    // For mobile: always show carousel
    // For desktop: show simple grid if 3 or fewer images
    if (!isMobile && images.length <= 3) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
          {images.map((image, index) => (
            <div key={image.id || index} className="relative group">
              <ServerImage
                src={image.image_url}
                alt={image.image_title || `${altPrefix} - Image ${index + 1}`}
                fallback="/blank.png"
                className="w-full h-106 object-cover transition-transform duration-300 group-hover:scale-105"
              />
            </div>
          ))}
        </div>
      );
    }

    // Carousel for mobile (always) or desktop with more than 3 images
    return (
      <div className="relative mb-8">
        <div className="overflow-hidden">
          <div 
            className="flex transition-transform duration-300 ease-in-out gap-3"
            style={{ 
              transform: `translateX(-${currentIndex * (100 / imagesPerView)}%)`,
              width: `${(images.length / imagesPerView) * 100}%`
            }}
          >
            {images.map((image, index) => (
              <div 
                key={image.id || index} 
                className="relative group flex-shrink-0"
                style={{ width: `${100 / images.length}%` }}
              >
                <ServerImage
                  src={image.image_url}
                  alt={image.image_title || `${altPrefix} - Image ${index + 1}`}
                  fallback="/blank.png"
                  className="w-full h-96 md:h-106 object-cover transition-transform duration-300 group-hover:scale-105"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Navigation Arrows */}
        {totalSlides > 1 && (
          <>
            {/* Left Arrow */}
            <button
              onClick={prevSlide}
              disabled={currentIndex === 0}
              className={`absolute left-2 md:left-4 top-1/2 transform -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 bg-white rounded-full shadow-lg flex items-center justify-center transition-all z-10 ${
                currentIndex === 0 
                  ? 'opacity-50 cursor-not-allowed' 
                  : 'hover:bg-gray-50 hover:shadow-xl'
              }`}
              style={{ 
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)' 
              }}
            >
              <FaArrowLeft className="text-gray-700" size={14} />
            </button>

            {/* Right Arrow */}
            <button
              onClick={nextSlide}
              disabled={currentIndex >= totalSlides - 1}
              className={`absolute right-2 md:right-4 top-1/2 transform -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 bg-white rounded-full shadow-lg flex items-center justify-center transition-all z-10 ${
                currentIndex >= totalSlides - 1 
                  ? 'opacity-50 cursor-not-allowed' 
                  : 'hover:bg-gray-50 hover:shadow-xl'
              }`}
              style={{ 
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)' 
              }}
            >
              <FaArrowRight className="text-gray-700" size={14} />
            </button>
          </>
        )}
      </div>
    );
  };

  const fetchStudioDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${SERVER}/content/studio-media/${slug}`);
      setStudioItem(response.data);
    } catch (error) {
      console.error('Error fetching studio details:', error);
      if (error.response?.status === 404) {
        setError('Studio item not found');
      } else {
        setError('Failed to load studio details');
      }
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchStudioDetails();
  }, [fetchStudioDetails]); 

  const handleBackClick = () => {
    navigate('/studio');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex items-center justify-center h-64 pt-24">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !studioItem) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex flex-col items-center justify-center h-64 pt-24">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">{error || 'Studio item not found'}</h2>
          <button
            onClick={handleBackClick}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
          >
            <FaArrowLeft />
            Back to Studio
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4">
          {/* Header */}
          <div className="mb-12">
            <button
              onClick={handleBackClick}
              className="flex items-center text-2xl md:text-3xl gap-2 hover:text-gray-800 transition-colors mb-6"
            >
              <FaArrowLeft size={15} />
              {studioItem.title}
            </button>

            {/* Category Badge */}
            <div className="flex flex-wrap gap-3 mb-2 md:mb-8">
              <span className="px-3 py-1 border border-black text-black rounded-full text-sm font-medium">
                {studioItem.category}
              </span>
            </div>
          </div>

          {/* Studio Sections */}
          <div className="space-y-4 md:space-y-16">
            {/* Dynamic Sections */}
            {studioItem.sections && studioItem.sections.length > 0 ? (
              studioItem.sections.map((section) => (
                <section key={section.id}>
                  <div className="flex items-center gap-2 md:gap-4 mb-1 md:mb-8">
                    <h2 className="text-2xl md:text-4xl font-bold text-gray-900">{section.section_title}</h2>
                    {section.section_url && (
                      <a
                        href={section.section_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center bg-soft-gray rounded-full py-1 px-2 gap-2 hover:text-blue-800 transition-colors"
                      >
                        <IoIosLink size={14} className="text-xs text-primary-blue" />
                        <span className="text-sm text-primary-gray">Links</span>
                      </a>
                    )}
                    <div className="border-b border-black w-full max-md:hidden"></div>
                  </div>

                  <p className="text-base font-light max-w-2xl mb-4 md:mb-12 leading-relaxed">
                    {section.section_description}
                  </p>

                  {/* Section Tags */}
                  {section.section_tags && section.section_tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-8">
                      {section.section_tags.map((tag, index) => (
                        <span
                          key={index}
                          className="flex items-center gap-2 bg-soft-gray py-1 px-2 rounded-full text-primary-gray text-sm"
                        >
                          <span 
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: 'var(--primary-blue)' }}
                          ></span>
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Section Gallery - Carousel for more than 3 images */}
                  {section.gallery && section.gallery.length > 0 && (
                    <ImageCarousel 
                      images={section.gallery} 
                      altPrefix={section.section_title}
                    />
                  )}

                  {/* Section Properties List - Below gallery, aligned to right */}
                  {section.properties && section.properties.length > 0 && (
                    <div className="flex justify-center w-full md:justify-end">
                      <div className="bg-soft-gray rounded-2xl p-6 w-full md:max-w-md">
                        <div className="space-y-4">
                          {section.properties.map((property, index) => (
                            <div key={property.id || index} className="flex items-start gap-3">
                              <div className="w-6 h-6 flex items-center justify-center">
                                <IoIosCheckmarkCircle size={20} className="text-primary-blue" />
                              </div>
                              <div>
                                <span className="text-gray-700 font-medium">{property.property_title}</span>
                                {property.property_description && (
                                  <p className="text-sm text-gray-500 mt-1">{property.property_description}</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </section>
              ))
            ) : (
              // Fallback to legacy structure - show main gallery
              <>
                <section>
                  <div className="flex items-center gap-4 mb-8">
                    <h2 className="text-4xl font-bold text-gray-900">{studioItem.category}</h2>
                    <div className="border-b border-black w-full max-md:hidden"></div>
                  </div>

                  <p className="text-base font-light max-w-2xl mb-12 leading-relaxed">
                    {studioItem.caption || 'Creative studio photography'}
                  </p>

                  {/* Legacy Gallery */}
                  {studioItem.gallery && studioItem.gallery.length > 0 && (
                    <ImageCarousel 
                      images={studioItem.gallery.map((img, idx) => ({
                        id: idx,
                        image_url: typeof img === 'string' ? img : img.url,
                        image_title: typeof img === 'object' ? img.alt : studioItem.title
                      }))} 
                      altPrefix={studioItem.title}
                    />
                  )}
                </section>
              </>
            )}

            {/* Call to Action */}
            <section className="text-center py-16">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Ready to capture your vision?
              </h3>
              <button 
                onClick={() => navigate('/book-consultation')}
                className="inline-flex items-center gap-2 text-white px-4 md:px-8 py-2 md:py-4 rounded-full font-semibold hover:opacity-90 transition-all duration-300"
                style={{ 
                  backgroundColor: 'var(--primary-blue)',
                  boxShadow: '0 4px 12px rgba(29, 68, 233, 0.4)'
                }}
              >
                Book a session
                <div className="p-2 rounded-full bg-white">
                  <FaArrowLeft className="rotate-180 text-black" size={12} />
                </div>
              </button>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default StudioDetail;
